import { DEFAULT_UNIT_ID, Unit } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as UnitActions from '../../../../common/actions/unit-actions';
import { TranslatedField } from '../../components/translated-field';
import { TranslationPill } from '../../components/translation-pill';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface UnitsOverviewProps {
    onEdit: (unit: Unit) => void;
}

export const UnitsOverview = (props: UnitsOverviewProps) => {
    const dispatch = useThunkDispatch();
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const units = useAppSelector(state => state.units.units);
    const [searchParams, setSearchParams] = useSearchParams();

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const columns: PaloColumn<Unit>[] = [
        {
            key: 'translation',
            type: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: unit => (
                <TranslatedField
                    value={unit.translations}
                    selectedLocale={selectedLanguage}
                    fallback={`ID ${unit.key}`}
                />
            ),
        },
        {
            key: 'isTranslated',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.translation" />,
            cell: unit => <TranslationPill value={unit.translations} locales={availableLocales} />,
        },
    ];

    useEffect(() => {
        dispatch(UnitActions.fetchUnits(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = (unit: Unit) => {
        if (unit.id !== DEFAULT_UNIT_ID) {
            dispatch(UnitActions.deleteUnit(unit.id));
        }
    };

    return (
        <PaloListView<Unit, number>
            embedded={true}
            columns={columns}
            items={units.items}
            getRowId={unit => unit.id}
            emptyLabel="Keine Einheiten."
            rowActions={unit => [
                { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(unit) },
                // The default unit is what a product without one falls back to.
                ...(DEFAULT_UNIT_ID === unit.id
                    ? []
                    : [{ label: <FormattedMessage id="app.common.delete" />, onClick: () => handleDelete(unit) }]),
            ]}
            pagination={{
                count: units.total,
                page: Number(searchParams.get('page') ?? 1),
                limit: Number(searchParams.get('limit') ?? 50),
                onPageChange: page => updateParams({ page: String(page) }),
                onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
            }}
        />
    );
};
